<script>
import CelestialQuoteHistory from "@/components/CelestialQuoteHistory";
import RaPet from "./RaPet";
import RaPetRemembranceButton from "./RaPetRemembranceButton";

export default {
  name: "RaTab",
  components: {
    RaPet,
    RaPetRemembranceButton,
    CelestialQuoteHistory
  },
  data() {
    return {
      memoriesPerChunk: 0,
      showReality: false,
      isRaCapped: false,
      totalLevels: 0,
      showRemembrance: false,
      hasRemembrance: false,
      remembranceReq: 0,
      remembranceMult: 1,
      remembranceNerf: 1,
      petWithRemembrance: "",
      isRunning: false,
      memoryBoosts: "",
    };
  },
  computed: {
    laitelaUnlock: () => Laitela.isUnlocked,
    pets: () => [
      {
        pet: Ra.pets.teresa,
        scalingUpgradeVisible: () => Ra.unlocks.chargedInfinityUpgrades.isUnlocked,
        scalingUpgradeText: () => `Вы можете зарядить ${ruQuantifyInt(
          "Улучшение Бесконечности,Улучшения Бесконечности,Улучшений Бесконечности", Ra.totalCharges)}.`,
      },
      {
        pet: Ra.pets.effarig,
        scalingUpgradeVisible: () => AlchemyResources.all.filter(r => r.isUnlocked).length > 0,
        scalingUpgradeText: () => {
          const resources = AlchemyResources.all.filter(r => r.isUnlocked).length;
          return `Вы открыли ${ruQuantifyInt(
            "Алхимический Ресурс,Алхимических Ресурса,Алхимических Ресурсов", resources)}.`;
        },
      },
      {
        pet: Ra.pets.enslaved,
        scalingUpgradeVisible: () => Ra.unlocks.improvedStoredTime.isUnlocked,
        scalingUpgradeText: () => `Накопленное игровое время
          ${formatX(Ra.unlocks.improvedStoredTime.effects.gameTimeAmplification.effectOrDefault(1), 2)} и реальное время
          +${formatInt(Ra.unlocks.improvedStoredTime.effects.realTimeCap.effectOrDefault(0) / (1000 * 3600))} часов`,
      },
      {
        pet: Ra.pets.v,
        scalingUpgradeVisible: () => Ra.unlocks.unlockHardV.isUnlocked,
        scalingUpgradeText: () => {
          const triadCount = Ra.unlocks.unlockHardV.effectOrDefault(0);
          return `Вы разблокировали ${ruQuantifyInt(
            "Исследование Триады,Исследования Триады,Исследований Триады", triadCount)}.`;
        },
      }
    ],
    petStyle() {
      // When importing saves from eng version, they clash and petWithRem contains english name... bad...
      try {
        return {
          color: (this.petWithRemembrance === "")
            ? "white"
            : this.pets.find(pet => pet.pet.name === this.petWithRemembrance).pet.color,
        };
      } catch {
        return {
          color: "white",
        };
      }
    },
    runButtonClassObject() {
      return {
        "c-ra-run-button__icon": true,
        "c-ra-run-button__icon--running": this.isRunning,
        "c-celestial-run-button--clickable": !this.isDoomed,
        "o-pelle-disabled-pointer": this.isDoomed
      };
    },
    runDescription() {
      return GameDatabase.celestials.descriptions[4].effects().replace(/^\w/u, c => c.toUpperCase()).split("\n");
    },
    memoryDescription() {
      return `Внутри Реальности Ра, Куски Памяти для Воспоминаний Божеств будут
        сгенерированы основываясь на количестве определённых ресурсов.`;
    },
    isDoomed: () => Pelle.isDoomed,
  },
  methods: {
    update() {
      this.memoriesPerChunk = Ra.productionPerMemoryChunk;
      this.isRaCapped = Ra.totalPetLevel === 100;
      this.totalLevels = Ra.totalPetLevel;
      this.showRemembrance = Ra.unlocks.effarigUnlock.canBeApplied;
      this.hasRemembrance = Ra.remembrance.isUnlocked;
      this.remembranceReq = Ra.remembrance.requiredLevels;
      this.remembranceMult = Ra.remembrance.multiplier;
      this.remembranceNerf = Ra.remembrance.nerf;
      this.petWithRemembrance = Ra.petWithRemembrance;
      this.isRunning = Ra.isRunning;
      this.memoryBoosts = Ra.memoryBoostResources;
    },
    startRun() {
      if (this.isDoomed) return;
      Modal.celestials.show({ name: "Ра", number: 4 });
    },
    toggleMode() {
      Ra.toggleMode();
    }
  },
};
</script>

<template>
  <div class="l-ra-celestial-tab">
    <div class="c-ra-memory-header">
      <CelestialQuoteHistory celestial="ra" />
      <div v-if="!isRaCapped">
        Каждый Кусок Воспоминаний генерирует одно Воспоминание в секунду<span v-if="memoriesPerChunk > 1">,
          которое повышено до {{ ruQuantify(
            "Воспоминания,Воспоминаний,Воспоминаний", memoriesPerChunk, 2, 3) }} в секунду</span>.
        <br>
        Накопление реального времени предотвращает генирацию от Кусков Воспоминаний, но Воспоминания всё ещё будут
        зарабатываться обычным образом.
        <span v-if="memoriesPerChunk > 1">
          <br>
          Это повышено из-за {{ memoryBoosts }}.
        </span>
      </div>
      <div v-else>
        Все воспоминания были восстановлены.
      </div>
    </div>
    <div>
      Наведите мышкой на иконки под полосами чтобы посмотреть описания улучшений,
      <br>
      наведите мышкой на значки <i class="fas fa-question-circle" /> для информации об определённом ресурсе.
    </div>
    <div class="l-ra-all-pets-container">
      <RaPet
        v-for="(pet, i) in pets"
        :key="i"
        :pet-config="pet"
      />
    </div>
    <div class="l-ra-non-pets">
      <button class="c-ra-run-button">
        <h2 :class="{ 'o-pelle-disabled': isDoomed }">
          <span v-if="isRunning">Вы в Реальности Ра</span>
          <span v-else>Начать Реальность Ра</span>
        </h2>
        <div
          :class="runButtonClassObject"
          @click="startRun"
        >
          <span class="c-ra-run-button__icon__sigil fas fa-sun" />
        </div>
        <span
          v-for="(line, lineId) in runDescription"
          :key="lineId + '-ra-run-desc'"
        >
          {{ line }}
        </span>
        <br>
        <span>
          {{ memoryDescription }}
        </span>
      </button>
      <div
        v-if="showRemembrance && !isRaCapped"
        class="c-ra-remembrance-unlock"
      >
        <h1 :style="petStyle">
          Вспоминаемость
        </h1>
        <span :style="petStyle">
          Божество, которому дана Вспоминаемость, получит {{ formatX(remembranceMult) }} заработок Кусков Памяти. Другие
          Божества получат {{ formatX(remembranceNerf, 1, 1) }} заработок Кусков Памяти.
        </span>
        <div
          v-if="hasRemembrance"
          class="c-ra-remembrance-unlock-inner"
        >
          <RaPetRemembranceButton
            v-for="(pet, i) in pets"
            :key="i"
            :pet-config="pet"
          />
        </div>
        <div
          v-else
          class="c-ra-remembrance-unlock-inner"
        >
          Разблокируется получением {{ formatInt(remembranceReq) }} уровней Воспоминаний Божеств
          (вам не хватает {{ formatInt(remembranceReq - totalLevels) }})
        </div>
      </div>
    </div>
  </div>
</template>
